import { Slider } from "@/components/ui/slider";
import { FormLabel, FormMessage } from "@/components/ui/form";
import { useState, useRef, useEffect } from "react";

interface TimeRangeSliderProps {
  loadInTime?: string;
  startTime: string;
  endTime: string;
  onLoadInChange: (value: string) => void;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  errors?: {
    loadIn?: string;
    start?: string;
    end?: string;
  };
}

const TimeRangeSlider = ({
  loadInTime,
  startTime,
  endTime,
  onLoadInChange,
  onStartChange,
  onEndChange,
  errors
}: TimeRangeSliderProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDraggingStart, setIsDraggingStart] = useState(false);
  
  // Timeline goes from 6am to 4am next day (22 hours)
  const TIMELINE_START = 6 * 60; // 6am in minutes
  const TIMELINE_END = (24 + 4) * 60; // 4am next day in minutes from midnight
  const TIMELINE_DURATION = TIMELINE_END - TIMELINE_START; // 1320 minutes (22 hours)

  // Convert time string to minutes since 6am
  const timeToSliderValue = (timeStr: string): number => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;
    
    // Handle times after midnight (0-4am becomes 24-28 hours)
    if (hours >= 0 && hours < 6) {
      totalMinutes += 24 * 60;
    }
    
    return totalMinutes - TIMELINE_START;
  };

  // Convert slider value (minutes since 6am) back to time string
  const sliderValueToTime = (value: number): string => {
    const totalMinutes = value + TIMELINE_START;
    const hours = Math.floor(totalMinutes / 60) % 24;
    const mins = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Format time for display (12-hour with AM/PM, with next day indicator)
  const formatTimeDisplay = (timeStr: string): string => {
    if (!timeStr) return "--:--";
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const nextDay = hours >= 0 && hours < 6 ? ' +1' : '';
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}${nextDay}`;
  };

  const startValue = timeToSliderValue(startTime);
  const endValue = timeToSliderValue(endTime);

  const hasError = errors?.start || errors?.end;

  const handleSliderChange = (values: number[]) => {
    // Round to nearest 15 minutes
    let [newStart, newEnd] = values.map(v => Math.round(v / 15) * 15);
    
    // Enforce constraints:
    // 1. End must be at least 1 hour (60 min) after start
    if (newEnd - newStart < 60) {
      newEnd = Math.min(TIMELINE_DURATION, newStart + 60);
    }
    
    // Auto-calculate load-in as 4 hours (240 min) before start
    const newLoadIn = Math.max(0, newStart - 240);
    
    // Apply the constrained values
    onLoadInChange(sliderValueToTime(newLoadIn));
    onStartChange(sliderValueToTime(newStart));
    onEndChange(sliderValueToTime(newEnd));
  };

  // Generate tick marks (every hour)
  const tickMarks = [];
  for (let i = 0; i <= TIMELINE_DURATION; i += 60) {
    tickMarks.push(i);
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">Start:</span>
          <span className="font-semibold text-primary">{formatTimeDisplay(startTime)}</span>
          <span className="text-muted-foreground">→</span>
          <span className="text-muted-foreground">End:</span>
          <span className="font-semibold">{formatTimeDisplay(endTime)}</span>
        </div>
      </div>
      
      <div className="space-y-1">
        <Slider
          value={[startValue, endValue]}
          onValueChange={handleSliderChange}
          min={0}
          max={TIMELINE_DURATION}
          step={15}
          className="w-full"
        />
        
        <div className="relative w-full px-1 mt-1">
          <div className="absolute inset-x-0 flex text-[9px] text-muted-foreground/50">
            <span style={{ position: 'absolute', left: '0%' }}>6AM</span>
            <span style={{ position: 'absolute', left: '27.3%' }}>12PM</span>
            <span style={{ position: 'absolute', left: '54.5%' }}>6PM</span>
            <span style={{ position: 'absolute', left: '81.8%' }}>12AM</span>
            <span style={{ position: 'absolute', left: '100%', transform: 'translateX(-100%)' }}>4AM</span>
          </div>
        </div>
      </div>

      {/* Error Messages */}
      {(errors?.loadIn || errors?.start || errors?.end) && (
        <div className="space-y-1">
          {errors?.loadIn && <FormMessage>{errors.loadIn}</FormMessage>}
          {errors?.start && <FormMessage>{errors.start}</FormMessage>}
          {errors?.end && <FormMessage>{errors.end}</FormMessage>}
        </div>
      )}
    </div>
  );
};

export default TimeRangeSlider;
